import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";

import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { SegmentationService } from "../../services/segmentation/SegmentationService";
import {
  closestSegmentationsToTime,
  drawSegmentations,
} from "../../helpers/SegmentationRenderer";

interface ImageSegmentationOverlayProps {
  imageSrc: string;
  // Detection time into the video, in seconds — used to pick the closest masks.
  timestampSeconds: number;
  videoSegmentation: SegmentationService;
  // Pre-filtered (by confidence option) segmentations from the parent.
  filteredSegmentations: Segmentation[];
  // A detection's stored time rarely matches a segmentation timestamp exactly, so
  // allow a looser match window than the video overlay's 0.15s.
  matchThreshold?: number;
  imgStyle?: React.CSSProperties;
}

// Renders a captured detection frame with the segmentation damage polygons for that
// frame's timestamp drawn on top, using the same draw helper as the video overlay.
// The canvas overlays the displayed <img> and rescales with it (ResizeObserver), so
// toggling area/contour/box or hiding a damage class updates reactively without
// re-capturing the frame.
export default observer(function ImageSegmentationOverlay(
  props: ImageSegmentationOverlayProps
) {
  const {
    imageSrc,
    timestampSeconds,
    videoSegmentation,
    filteredSegmentations,
    matchThreshold = 1.0,
    imgStyle,
  } = props;

  const { showArea, showContour, showBox, showLabels } = videoPlayerService;

  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const detectedGroupCodes = JSON.stringify(videoSegmentation?.detectedGroupCodes);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const imageSize = videoSegmentation?.vaData.segmentationDetails?.imageSize;
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (!size.width || !size.height || !imageSize?.width || !imageSize?.height) return;

    const scaleX = size.width / imageSize.width;
    const scaleY = size.height / imageSize.height;

    videoSegmentation?.detectedGroupCodes?.forEach((groupCode) => {
      if (!groupCode.isVisible || !groupCode.isDetected) return;

      const forCode = filteredSegmentations.filter((s) => s.groupCode === groupCode.code);
      const closest = closestSegmentationsToTime(forCode, timestampSeconds, matchThreshold);
      if (!closest.length) return;

      const color = videoSegmentation?.getGroupCodeColors()[groupCode.code.toLowerCase()];
      drawSegmentations(context, closest, color, scaleX, scaleY, {
        showArea,
        showContour,
        showBox,
        label: showLabels ? groupCode.code : undefined,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size,
    showArea,
    showContour,
    showBox,
    showLabels,
    filteredSegmentations,
    timestampSeconds,
    matchThreshold,
    detectedGroupCodes,
    videoSegmentation,
  ]);

  // Keep the canvas pixel size matched to the displayed image as it reflows.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const measure = () =>
      setSize({ width: img.clientWidth, height: img.clientHeight });

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(img);
    return () => observer.disconnect();
  }, [imageSrc]);

  useEffect(() => {
    draw();
  }, [draw]);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <img
        ref={imgRef}
        src={imageSrc}
        alt="frame"
        onLoad={() =>
          setSize({
            width: imgRef.current?.clientWidth ?? 0,
            height: imgRef.current?.clientHeight ?? 0,
          })
        }
        style={{ width: "100%", height: "auto", objectFit: "cover", display: "block", ...imgStyle }}
      />
      <canvas
        ref={canvasRef}
        width={size.width}
        height={size.height}
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
      />
    </Box>
  );
});
